import type { PlayerMovementState } from "./player-movement";

export interface GrowthTable {
  readonly sizes: readonly number[];
  readonly thresholds: readonly number[];
}

export interface PlayerGrowthProgress {
  readonly currentThreshold: number;
  readonly growth: number;
  readonly growthInLevel: number;
  readonly level: number;
  readonly nextThreshold: number | null;
  readonly requiredInLevel: number | null;
  readonly size: number;
}

const requireGrowth = (growth: number): void => {
  if (!Number.isFinite(growth) || growth < 0) {
    throw new RangeError("Player growth must be non-negative and finite");
  }
};

const requireGrowthAmount = (growth: number): void => {
  if (!Number.isFinite(growth) || growth <= 0) {
    throw new RangeError("Growth amount must be positive and finite");
  }
};

const requireLevel = (level: number, maximumLevel: number): void => {
  if (!Number.isSafeInteger(level) || level < 1 || level > maximumLevel) {
    throw new RangeError(
      `Player level must be an integer from 1 through ${String(maximumLevel)}`,
    );
  }
};

const requireTableEntry = (
  values: readonly number[],
  index: number,
  name: string,
): number => {
  const value = values[index];
  if (value === undefined) {
    throw new Error(`${name} is missing for level ${String(index + 1)}`);
  }
  return value;
};

const levelIndexForGrowth = (
  growth: number,
  thresholds: readonly number[],
): number => {
  let lowerIndex = 0;
  let upperIndex = thresholds.length;
  while (lowerIndex + 1 < upperIndex) {
    const middleIndex = Math.floor((lowerIndex + upperIndex) / 2);
    const threshold = requireTableEntry(
      thresholds,
      middleIndex,
      "Growth threshold",
    );
    if (growth < threshold) {
      upperIndex = middleIndex;
    } else {
      lowerIndex = middleIndex;
    }
  }
  return lowerIndex;
};

export const getPlayerGrowthProgress = (
  growth: number,
  table: GrowthTable,
): PlayerGrowthProgress => {
  requireGrowth(growth);
  const levelIndex = levelIndexForGrowth(growth, table.thresholds);
  const currentThreshold = requireTableEntry(
    table.thresholds,
    levelIndex,
    "Growth threshold",
  );
  const nextThreshold = table.thresholds[levelIndex + 1] ?? null;
  return {
    currentThreshold,
    growth,
    growthInLevel: growth - currentThreshold,
    level: levelIndex + 1,
    nextThreshold,
    requiredInLevel:
      nextThreshold === null ? null : nextThreshold - currentThreshold,
    size: requireTableEntry(table.sizes, levelIndex, "Player size"),
  };
};

const withGrowthProgress = (
  state: PlayerMovementState,
  progress: PlayerGrowthProgress,
): PlayerMovementState => ({
  ...state,
  collision: { radius: progress.size },
  growth: progress.growth,
  level: progress.level,
  size: progress.size,
});

export const addPlayerGrowth = (
  state: PlayerMovementState,
  amount: number,
  table: GrowthTable,
): PlayerMovementState => {
  requireGrowthAmount(amount);
  const growth = state.growth + amount;
  requireGrowth(growth);
  return withGrowthProgress(state, getPlayerGrowthProgress(growth, table));
};

export const setPlayerGrowth = (
  state: PlayerMovementState,
  growth: number,
  table: GrowthTable,
): PlayerMovementState =>
  withGrowthProgress(state, getPlayerGrowthProgress(growth, table));

export const setPlayerLevel = (
  state: PlayerMovementState,
  level: number,
  table: GrowthTable,
): PlayerMovementState => {
  requireLevel(level, table.thresholds.length);
  const growth = requireTableEntry(
    table.thresholds,
    level - 1,
    "Growth threshold",
  );
  return withGrowthProgress(state, getPlayerGrowthProgress(growth, table));
};
